import React from 'react';

/**
 * "Office Use Only" style summary — mirrors the HFF Annual Cashflow Excel tab.
 * Shows total income, expenses by category, auto-deducted items,
 * and net cashflow.
 */

const fmt = (v) =>
  `$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const num = (v) => (typeof v === 'number' ? v : Number(v) || 0);

/* ── Helpers to sum a section ────────────────────────────────────── */

const sumIncome = (data) => {
  if (!data?.sources) return 0;
  return data.sources.reduce((s, src) => s + num(src.amount), 0);
};

const sumSimple = (data, exclude = []) => {
  if (!data) return 0;
  return Object.entries(data)
    .filter(([k, v]) => typeof v === 'number' && !exclude.includes(k))
    .reduce((s, [, v]) => s + v, 0);
};

const sumAuto = (data, autoKeys) => {
  if (!data) return { total: 0, auto: 0, manual: 0 };
  let total = 0;
  let auto = 0;
  Object.entries(data).forEach(([k, v]) => {
    if (typeof v !== 'number') return;
    if (k.endsWith('_auto')) return; // skip the boolean-like flag keys
    total += v;
    const autoKey = k + '_auto';
    if (autoKeys.includes(autoKey) && data[autoKey]) {
      auto += v;
    }
  });
  return { total, auto, manual: total - auto };
};

const sumAutoTransport = (data) => {
  if (!data) return 0;
  let total = 0;
  ['auto_1', 'auto_2', 'auto_3'].forEach((k) => {
    if (data[k]?.amount) total += num(data[k].amount);
  });
  ['gas_fuel', 'license_registration', 'parking_tolls', 'service_maintenance', 'auto_insurance', 'public_transportation']
    .forEach((k) => { total += num(data[k]); });
  return total;
};

const sumCharity = (data) => {
  if (!data) return 0;
  return num(data.charitable_gifting) + num(data.tithes_church);
};

/* ── Section configs ─────────────────────────────────────────────── */

const MEDICAL_AUTO = ['dental_insurance_auto', 'medical_insurance_auto', 'vision_insurance_auto', 'prescription_insurance_auto'];
const INSURANCE_AUTO = ['life_insurance_auto', 'long_term_care_auto', 'disability_auto', 'umbrella_auto', 'other_insurance_auto'];
const SAVINGS_AUTO = ['retirement_savings_auto', 'hsa_fsa_auto'];

const computeSummary = (fd) => {
  if (!fd) return null;

  const income = sumIncome(fd.income);

  // Sections with auto-deduct tracking
  const medical = sumAuto(fd.medical, MEDICAL_AUTO);
  const insurance = sumAuto(fd.insurance, INSURANCE_AUTO);
  const savings = sumAuto(fd.savings, SAVINGS_AUTO);

  // Simple sections
  const housing = sumSimple(fd.housing);
  const bills = sumSimple(fd.bills_utilities);
  const auto = sumAutoTransport(fd.auto_transport);
  const entertainment = sumSimple(fd.entertainment_travel);
  const personal = sumSimple(fd.personal_family_care);
  const charity = sumCharity(fd.charity);
  const legal = sumSimple(fd.legal_professional);
  const kids = sumSimple(fd.kids_education);
  const pets = sumSimple(fd.pets);
  const debts = sumSimple(fd.debts);

  const totalExpenses =
    housing + bills + auto +
    medical.manual + insurance.manual +
    entertainment + personal + charity + legal + kids + pets + debts +
    savings.manual;

  const totalAutoDeducted = medical.auto + insurance.auto + savings.auto;
  const netCashflow = income - totalExpenses - totalAutoDeducted;

  const sections = [
    { label: 'Housing', value: housing, icon: 'bi-house' },
    { label: 'Bills & Utilities', value: bills, icon: 'bi-plug' },
    { label: 'Auto & Transport', value: auto, icon: 'bi-car-front' },
    { label: 'Medical (out-of-pocket)', value: medical.manual, icon: 'bi-heart-pulse' },
    { label: 'Insurance (out-of-pocket)', value: insurance.manual, icon: 'bi-shield-check' },
    { label: 'Entertainment & Travel', value: entertainment, icon: 'bi-airplane' },
    { label: 'Personal & Family Care', value: personal, icon: 'bi-people' },
    { label: 'Kids & Education', value: kids, icon: 'bi-mortarboard' },
    { label: 'Pets', value: pets, icon: 'bi-emoji-heart-eyes' },
    { label: 'Charity', value: charity, icon: 'bi-gift' },
    { label: 'Legal / Professional', value: legal, icon: 'bi-briefcase' },
    { label: 'Debts', value: debts, icon: 'bi-credit-card' },
    { label: 'Savings (out-of-pocket)', value: savings.manual, icon: 'bi-piggy-bank' },
  ];

  const autoBreakdown = [];
  if (medical.auto) autoBreakdown.push({ label: 'Medical Insurance', value: medical.auto });
  if (insurance.auto) autoBreakdown.push({ label: 'Insurance', value: insurance.auto });
  if (savings.auto) autoBreakdown.push({ label: 'Retirement / HSA', value: savings.auto });

  return { income, sections, totalExpenses, totalAutoDeducted, autoBreakdown, netCashflow };
};

/* ── Component ───────────────────────────────────────────────────── */

const CashflowSummaryView = ({ formData }) => {
  const summary = computeSummary(formData);
  if (!summary) {
    return <p className="text-muted fst-italic">No data to summarize.</p>;
  }

  const { income, sections, totalExpenses, totalAutoDeducted, autoBreakdown, netCashflow } = summary;
  const netColor = netCashflow >= 0 ? 'text-success' : 'text-danger';

  return (
    <div>
      {/* Income */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
        <h6 className="mb-0"><i className="bi bi-cash-coin me-2"></i>Total Annual Income</h6>
        <span className="fs-5 fw-bold text-success">{fmt(income)}</span>
      </div>

      {/* Expense breakdown */}
      <h6 className="text-muted mb-2">Annual Expenses</h6>
      <table className="table table-sm mb-3">
        <tbody>
          {sections.filter(s => s.value > 0).map(({ label, value, icon }) => (
            <tr key={label}>
              <td><i className={`bi ${icon} me-2 text-muted`}></i>{label}</td>
              <td className="text-end fw-semibold">{fmt(value)}</td>
            </tr>
          ))}
          <tr className="table-light fw-bold">
            <td>Total Out-of-Pocket Expenses</td>
            <td className="text-end">{fmt(totalExpenses)}</td>
          </tr>
        </tbody>
      </table>

      {/* Auto-deducted breakdown */}
      {totalAutoDeducted > 0 && (
        <>
          <h6 className="text-muted mb-2">
            <i className="bi bi-arrow-repeat me-2"></i>Auto-Deducted from Wages
          </h6>
          <table className="table table-sm mb-3">
            <tbody>
              {autoBreakdown.map(({ label, value }) => (
                <tr key={label}>
                  <td className="text-muted">{label}</td>
                  <td className="text-end fw-semibold">{fmt(value)}</td>
                </tr>
              ))}
              <tr className="table-light fw-bold">
                <td>Total Auto-Deducted</td>
                <td className="text-end">{fmt(totalAutoDeducted)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* Net cashflow */}
      <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-2">
        <h5 className="mb-0">Net Annual Cashflow</h5>
        <span className={`fs-4 fw-bold ${netColor}`}>
          {netCashflow < 0 ? '−' : ''}{fmt(netCashflow)}
        </span>
      </div>
      <div className="text-muted small mt-1">
        Income ({fmt(income)}) − Expenses ({fmt(totalExpenses)}) − Auto-Deducted ({fmt(totalAutoDeducted)})
      </div>

      {/* Monthly equivalent */}
      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
        <span className="text-muted">Monthly Equivalent</span>
        <span className={`fw-semibold ${netColor}`}>
          {netCashflow < 0 ? '−' : ''}{fmt(Math.round(Math.abs(netCashflow) / 12))}/mo
        </span>
      </div>
    </div>
  );
};

export default CashflowSummaryView;
